import '@fortawesome/fontawesome-svg-core/styles.css'
import 'react-datepicker/dist/react-datepicker.css'

import { Eye } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from 'src/hooks/auth'
import ModalConfirmOperation from '../../components/ModalConfirmOperation'
import { Skeleton } from '../../components/skeleton'
import { service as api } from '../../services/ajio'
import { currencyFormat } from '../../utils/currencyFormat'
import { dateFormatDaily } from '../../utils/dateFormat'
import type { ExtractItem } from '../../utils/extractDescription'
import './style.css'
import { Container, Content, PrintButtonIcon, TableWrapper } from './styles'

import { DatePicker } from '@mui/x-date-pickers'
import type { PaginationProps } from 'antd'
import { Button, Flex, Pagination } from 'antd'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import 'react-datepicker/dist/react-datepicker.css'
import { getExtract } from 'src/requests/export-extract'
import { formatTransactionType } from 'src/utils/formatTransactionType'
import { separateTransactions } from 'src/utils/separateTransactions'
import './style.css'

export interface Transaction {
  id: string
  description: string
  balanceAfter: number
  created_at: string
  status: string
  credit: boolean
  direction: string
  data: any
  GraphicAccount: any
  createdAt: string
  category: string
  amount: number
  type: string
  transactionData: {
    clientNamePayer: string
    clientName: string
    keyTarget: string
  }
}

export interface Detail {
  id: string
  credit: boolean
  amount: number
  description: string
  rateValue: number
  consignorName: string
  consignorDocument: string
  consignorBankNumber: string
  consignorBankBranch: string
  consignorBankAccount: string
  consignorBankAccountDigit: string
  beneficiaryName: string
  beneficiaryDocument: string
  beneficiaryBankNumber: string
  beneficiaryBankBranch: string
  beneficiaryBankAccount: string
  beneficiaryBankAccountDigit: string
  status: string
  date: string
}

export interface ShowTransaction {
  category: string
  transactionData: any
  detail: any
  transaction: any
  createdAt: string
  amount: string
}

type TransactionData = {
  id: string
  type: string
  grossAmount: number
  netAmount: number
  taxTotal: number
  isAwaitingTransfer: boolean
  installments: number
  machineId: string
  machineSerial: string
  transactionId: string
  created_at: string
  updated_at: string
  status: number
}

export const Receipt = ({ itemsPerPage = 10 }) => {
  const navigate = useNavigate()
  const { account } = useAuth()
  const token = localStorage.getItem('@stricv2:token') as string
  const user = JSON.parse(localStorage.getItem('@stricv2:user') as string)

  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState<ExtractItem[]>([])
  const [selectedItem, setSelectedItem] = useState<ExtractItem>()
  const [alreadyOpen, setAlreadyOpen] = useState<boolean>(false)

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState<boolean>(false)

  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const [isExportExcel, setIsExportExcel] = useState<boolean>(false)

  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [perPage, setPerPage] = useState(10)

  const [startDate, setStartDate] = useState(
    dayjs().subtract(1, 'day').locale('pt-br')
  )
  const [endDate, setEndDate] = useState(dayjs().locale('pt-br'))

  const [searchParams] = useSearchParams()
  const transactionId = searchParams.get('transactionId')

  const handleRowClick = (transaction: TransactionData) => {
    navigate(`/transactions/${transaction.transactionId}`, {
      state: { transactionData: transaction },
    })
  }

  useEffect(() => {
    fetchCustomDateTransactions()
  }, [])

  const handleStartDateChange = (newDate: dayjs.Dayjs | null) => {
    if (newDate) setStartDate(newDate)
  }

  const handleEndDateChange = (newDate: dayjs.Dayjs | null) => {
    if (newDate) setEndDate(newDate)
  }

  const fetchCustomDateTransactions = () => {
    setLoading(true)

    api
      .get(`/transactions?start=${startDate}&end=${endDate}`)
      .then((res) => {
        const transactions = res.data.transactions.data as ExtractItem[]
        const { orderedTransactions } = separateTransactions(transactions)

        setItems(orderedTransactions)
        setTotal(res.data.transactions.total)

        if (transactionId && !alreadyOpen) {
          const tx = orderedTransactions.find((t) => t.id === transactionId)
          if (tx) {
            setSelectedItem(tx)
            setIsOpen(true)
            setAlreadyOpen(true)
          }
        }
        setLoading(false)
      })
      .catch((error) => {
        console.error('Erro ao buscar transações:', error)
        setLoading(false)
      })
  }
  const [modalIsOpen, setIsOpen] = useState(false)

  function afterOpenModal() {}

  function closeModal() {
    setIsOpen(false)
  }

  const handleClickExportPDF = () => {
    handleExport('pdf')
  }

  const handleClickExportExcel = () => {
    handleExport('excel')
  }

  const fetchExportTransactions = async () => {
    try {
      const response = await api.get(
        `/transactions?start=${startDate}&end=${endDate}`
      )
      return response.data.transactions.data
    } catch (err) {
      console.log(err)
    }
  }

  async function handleExport(type: string) {
    const startDateFormatted = startDate.toISOString().split('T')[0] // "2023-06-01"
    const endDateFormatted = endDate.toISOString().split('T')[0]

    if (type === 'pdf') {
      setIsExportingPdf(true)

      const transaction = await getExtract(
        startDateFormatted,
        endDateFormatted,
        user.id,
        'pdf'
      ).finally(() => {
        setIsExportingPdf(false)
      })

      console.log(transaction)

      const linkSource = `data:application/pdf;base64,${transaction?.pdfBase64}`
      const downloadLink = document.createElement('a')

      downloadLink.href = linkSource
      downloadLink.download = `extrato.pdf`
      downloadLink.click()
    } else if (type === 'excel') {
      setIsExportExcel(true)
      await getExtract(
        startDateFormatted,
        endDateFormatted,
        account.id,
        'excel'
      ).finally(() => {
        setIsExportExcel(false)
      })
    }
  }

  function handleClickRow(item: ExtractItem) {
    setSelectedItem(item)
  }

  const handleCloseDownloadModal = () => {
    setIsDownloadModalOpen(false)
    fetchCustomDateTransactions()
  }

  useEffect(() => {
    if (process.env.NODE_ENV == 'development') {
      console.log('Transacao selecionada', selectedItem)
    }
  }, [selectedItem])

  const href = window.location.href

  const onShowSizeChange: PaginationProps['onShowSizeChange'] = (
    current,
    pageSize
  ) => {
    setPerPage(pageSize)
  }

  const startIdx = (page - 1) * perPage
  const endIdx = startIdx + perPage
  const paginatedItems = items.slice(startIdx, endIdx)

  return (
    <>
      <ModalConfirmOperation
        user={user}
        closeModal={closeModal}
        modalIsOpen={modalIsOpen}
        afterOpenModal={() => {}}
        data={selectedItem}
      />
      <Container className="w-full">
        <Content className="w-full h-full padding-container items-center justify-center">
          <div className="w-full flex flex-row items-center justify-between max-w-[96%] mb-[1%]">
            <Flex align="center" gap={10}>
              <div className="w-full flex flex-row h-full items-center gap-4">
                <DatePicker
                  slotProps={{
                    textField: {
                      InputProps: {
                        style: {
                          fontSize: '1.5vw',
                          width: '14vw',
                          height: '6vh',
                        },
                      },
                      InputLabelProps: {
                        style: { fontSize: '1vw' },
                      },
                    },
                  }}
                  label="Data Inicial"
                  value={startDate}
                  onChange={handleStartDateChange}
                  format="DD/MM/YYYY"
                />
                <span className="p-text">até</span>
                <DatePicker
                  slotProps={{
                    textField: {
                      InputProps: {
                        style: {
                          fontSize: '1.5vw',
                          width: '14vw',
                          height: '6vh',
                        },
                      },
                      InputLabelProps: {
                        style: { fontSize: '1vw' },
                      },
                    },
                  }}
                  label="Data Final"
                  value={endDate}
                  onChange={handleEndDateChange}
                  format="DD/MM/YYYY"
                />
              </div>
              <Button
                className="bg-[#AA7339] border-radius-container items-center flex justify-center w-full px-[3%] py-[4%] p-text text-white"
                onClick={fetchCustomDateTransactions}
              >
                Filtrar
              </Button>
            </Flex>
            <div className="flex items-center w-full max-w-[30%] gap-4">
              <Button
                onClick={handleClickExportPDF}
                className="bg-[#AA7339] border-radius-container items-center flex justify-center w-full px-[3%] py-[5%] p-text text-white"
                style={{
                  opacity: items.length === 0 ? 0.7 : 1,
                  cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                }}
                disabled={items.length === 0}
              >
                {isExportingPdf ? 'Exportando...' : 'Exportar extrato PDF'}
              </Button>
              <Button
                onClick={handleClickExportExcel}
                className="bg-[#AA7339] border-radius-container items-center flex justify-center w-full px-[3%] py-[5%] p-text text-white"
                style={{
                  opacity: items.length === 0 ? 0.7 : 1,
                  cursor: items.length === 0 ? 'not-allowed' : 'pointer',
                }}
                disabled={items.length === 0}
              >
                {isExportExcel ? 'Exportando...' : 'Exportar extrato Excel'}
              </Button>
            </div>
          </div>
          <div className="w-full">
            <TableWrapper className="w-full flex flex-col h-full items-center justify-center ">
              {loading ? (
                <div className="height-skeleton bg-[#FFFFFF] w-full items-center justify-center mx-auto">
                  <Skeleton space={3} isActive={loading} repeat={4} />
                </div>
              ) : (
                <>
                  <table>
                    {' '}
                    <thead>
                      {!loading && (
                        <tr className="text-left font-semibold rounded-t-lg">
                          <th className="table-title">Data</th>
                          <th className="table-title">Tipo</th>
                          <th className="table-title">Transação</th>
                          <th className="table-title">Beneficiário/Pagador</th>
                          <th className="table-title">Valor</th>
                          <th className="table-title">Comprovante</th>
                        </tr>
                      )}
                    </thead>
                  </table>
                  <div className="overflow-y-auto height-table">
                    {items.length === 0 && !loading ? (
                      <td className="text-center py-4 w-full">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            Nenhum lançamento para o período selecionados
                          </h3>
                          <p className="mt-2 text-sm text-gray-500">
                            Selecione outro período ou faça uma transferência e
                            comece a usar agora mesmo
                          </p>
                        </div>
                      </td>
                    ) : (
                      <table className="w-full border-collapse">
                        <tbody>
                          {' '}
                          {items &&
                            paginatedItems.map((item) => (
                              <tr
                                key={item.id}
                                onClick={() => handleClickRow(item)}
                              >
                                <td className="table-text">
                                  {dateFormatDaily(item.created_at)}
                                </td>

                                <td className="table-text">
                                  <span>
                                    {item.direction === 'in'
                                      ? 'Credito'
                                      : 'Debito'}
                                  </span>
                                </td>

                                <td className="table-text text-center align-middle">
                                  {item.GraphicAccount &&
                                  item.GraphicAccount?.name ? (
                                    <span className="table-text">
                                      Control Account -{' '}
                                      {item.GraphicAccount.name}
                                    </span>
                                  ) : (
                                    <span className="table-text">
                                      {item.user?.name}
                                    </span>
                                  )}
                                  <br />
                                  <span
                                    className="table-text"
                                    style={{
                                      color:
                                        item.type === 'withdraw' &&
                                        user?.id === item?.data?.payer?.id
                                          ? '#ff0013'
                                          : item.direction === 'in' &&
                                            item.type !== 'internal'
                                          ? 'var(--green)'
                                          : item.direction === 'out' &&
                                            item.status === 'denied'
                                          ? '#ff0013'
                                          : 'black',
                                    }}
                                  >
                                    {formatTransactionType(item, user)}
                                  </span>{' '}
                                  <br />
                                  {item.type === 'withdraw'
                                    ? 'Transação interna'
                                    : item.description}
                                </td>
                                <td className="table-text text-center align-middle">
                                  {' '}
                                  {item.response === null &&
                                  item.title === 'Retirada' ? (
                                    <>
                                      <span
                                        className="table-text"
                                        style={{ color: 'darkgray' }}
                                      >
                                        C-A
                                      </span>
                                      <br />
                                      {item?.data?.payer?.name}
                                      <span className="table-text">
                                        <br />
                                        {item.description === 'Retirada'
                                          ? 'Saldo devolvido para a conta principal.'
                                          : item.GraphicAccount}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <br />
                                      <span className="table-text text-wrap break-words">
                                        {item.direction == 'in'
                                          ? `De: ${
                                              item?.response?.payer?.holder
                                                ?.name ||
                                              item?.response?.proof?.payer
                                                ?.holder?.name ||
                                              item?.data?.payer?.name ||
                                              item?.GraphicAccount?.name ||
                                              item?.response?.payer?.name ||
                                              item?.data?.payer?.holder?.name ||
                                              item?.GraphicAccount?.name ||
                                              item?.response?.payer?.name ||
                                              item?.beneficiary?.holder?.name ||
                                              item?.beneficiary?.name ||
                                              item?.data.beneficiary?.holder
                                                ?.name ||
                                              item?.data?.proof?.beneficiary
                                                ?.holder?.name ||
                                              item?.response?.data?.counterparty
                                                ?.name ||
                                              item?.data.data.payer.name ||
                                              '*sem nome*'
                                            }`
                                          : item.type === 'INTERNAL_TAX' ||
                                            'INTERNAL_WALLET_TRANSACTION'
                                          ? item.type !== 'INTERNAL_TAX'
                                            ? `Para: ${
                                                item?.response?.data
                                                  .counterparty?.name ||
                                                item?.data?.beneficiary?.name ||
                                                item?.data?.transfer
                                                  ?.destination_bank_account
                                                  ?.beneficiaryName ||
                                                item?.data?.beneficiaryName ||
                                                item?.data?.payment_info
                                                  ?.recipient_name ||
                                                'sem nome'
                                              }`
                                            : 'Para: Symbank'
                                          : //@ts-ignore
                                            `Para: ${
                                              item?.response?.beneficiary
                                                ?.holder?.name ||
                                              item?.response?.proof?.beneficiary
                                                ?.holder?.name ||
                                              item?.response?.beneficiary
                                                ?.name ||
                                              item?.data?.beneficiary?.name ||
                                              '*sem nome*'
                                            }`}
                                      </span>
                                    </>
                                  )}
                                </td>
                                <td className="table-text text-center align-middle">
                                  <span
                                    className="table-text"
                                    style={{
                                      color:
                                        item.type === 'withdraw' &&
                                        user?.id === item?.data?.payer?.id
                                          ? 'var(--red)'
                                          : item.type == 'internal'
                                          ? item?.data?.payer?.id == user?.id
                                            ? 'var(--red)'
                                            : 'var(--green)'
                                          : item.direction === 'in'
                                          ? 'var(--green)'
                                          : item.direction === 'out' &&
                                            item.status === 'denied'
                                          ? '#606060'
                                          : 'var(--red)',
                                      marginLeft: '8px',
                                    }}
                                  >
                                    {item.type === 'withdraw' &&
                                    user?.id === item?.data?.payer?.id ? (
                                      '-'
                                    ) : item.direction === 'out' &&
                                      item.status === 'denied' ? (
                                      <>&nbsp;&nbsp;</>
                                    ) : item.type === 'internal' ? (
                                      user?.id === item?.data?.payer?.id ? (
                                        '-'
                                      ) : (
                                        '+'
                                      )
                                    ) : item.direction === 'in' &&
                                      item.status !== 'denied' ? (
                                      '+'
                                    ) : (
                                      '-'
                                    )}
                                    {currencyFormat(item.amount)}
                                  </span>
                                </td>

                                <td className="table-text">
                                  <PrintButtonIcon>
                                    <Eye
                                      className="status-icon"
                                      color="rgb(170, 170, 170)"
                                      onClick={() => setIsOpen(true)}
                                      // color="var(--boxText)"
                                    />
                                  </PrintButtonIcon>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </TableWrapper>
          </div>
          {!loading && (
            <div className="flex flex-col w-full items-center">
              <Pagination
                showSizeChanger
                onShowSizeChange={onShowSizeChange}
                onChange={(e) => setPage(e)}
                defaultCurrent={page}
                total={total}
              />
            </div>
          )}
        </Content>
      </Container>
    </>
  )
}
